// =============================================================================
// ui/tabs/Practice.tsx — the "Fix it" gym. #59.
// -----------------------------------------------------------------------------
// Relocated from the old client's `renderPractice` (app/index.html:28699-28993),
// which is 295 lines and registered at :28992.
//
// 🔴 THE ISSUE THAT ASKED FOR THIS SAID BETA'S TAB WAS AN EMPTY SECTION WITH
// NOTHING TO PORT. That was wrong, and it was my error: I read the HTML, found
// `<section class="tab-section" data-tab="practice"></section>` empty at :3632,
// and stopped. EVERY tab in beta has an empty section tag — they are all filled
// in by JavaScript at render time. The migration's own header repeats the same
// mistake, still calling the storage "INERT".
//
// ── ⚠ WHAT PRACTICE IS, FROM THE PLAN THAT OWNS IT ──
//
//   **Practice is Backtesting with a different question.** Backtesting asks
//   "does this setup have edge?"; Practice asks "can I execute?". You select
//   completed trades, it imports a COPY, and the copy is what you grade.
//
// So: it does not discover sessions, and it never moves, hides or flags the
// original trade. A replay fill stays an ordinary trade, in or out of your stats
// according to its ACCOUNT's exclude-from-metrics switch — the same one SIM
// accounts use. The empty state says so, in beta's words, because that is the
// single most misunderstood thing about the feature.
//
// ── 🚨 THESE NUMBERS ARE NOT MONEY ──
//
// A session's P&L is what the trader WOULD have made. It is shaped exactly like
// a real figure and it sits in an app whose other screens are real, so every
// place it appears is labelled. Beta's coach prompt makes the same point to the
// model — it is told not to treat replay P&L as real money.
//
// ── 🔴 WHAT THIS PASS DOES NOT DO, NAMED ON SCREEN ──
//
//   Import trades      — the picker is the next increment. Without it no NEW
//                        session can be created, so the tab is read-and-grade
//                        over whatever is already there.
//   Coach this session — the AI review. Needs the day-brief prompt path.
//   The day report     — scorecard, intraday chart, performance strip and the
//                        leak read. That is `dayReportSections` reuse, which
//                        Day Overview already has, and it is its own piece.
//
// A tab silently missing three things is indistinguishable from a broken one.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { practiceSessionRows, type PracticeSessionRow } from '../../selectors/practice';
import { usePractice } from '../state/practice';
import { money, num, pct } from '../format';

/** Beta's grade set, in its order. */
const GRADES = ['A', 'B', 'C', 'D', 'F'] as const;

function GradeChip({ grade }: { grade: string }) {
  if (!grade) return null;
  return <span className={`pr-chip pr-chip-${grade.toLowerCase()}`}>{grade}</span>;
}

export function PracticeTab() {
  const { phase, sessions, trades, error, saveSession, removeSession } = usePractice();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const rows = useMemo(
    () => practiceSessionRows(sessions ?? [], trades ?? []),
    [sessions, trades],
  );
  // ⚠ Dismissed is DE-CLUTTER, not deletion — the sessions are still there and
  // the toggle brings them back. Filtering them out of existence would make
  // "Dismiss" read as a delete with a gentler word.
  const visible = useMemo(
    () => rows.filter((r) => showDismissed || !r.session.dismissed),
    [rows, showDismissed],
  );

  // ⚠ Selection follows the list. A session that was dismissed, deleted, or
  // filtered out from under the selection must not leave the detail pane
  // rendering a session the list no longer offers.
  useEffect(() => {
    if (visible.length === 0) { setSelectedId(null); return; }
    if (!selectedId || !visible.some((r) => r.session.id === selectedId)) {
      setSelectedId(visible[0]?.session.id ?? null);
    }
  }, [visible, selectedId]);

  const selected = visible.find((r) => r.session.id === selectedId) ?? null;
  const dismissedCount = rows.filter((r) => r.session.dismissed).length;

  if (phase === 'loading') return <p className="muted">Loading your practice sessions…</p>;

  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load Practice</h3>
        <p className="muted">Nothing has been changed. {error}</p>
      </div>
    );
  }

  const patch = async (row: PracticeSessionRow, next: Partial<{
    label: string; grade: string; notes: string; dismissed: boolean;
  }>) => {
    setSaveError(null);
    const { label, dismissed, ...extras } = next;
    const res = await saveSession({
      ...row.session,
      ...(label === undefined ? {} : { label }),
      ...(dismissed === undefined ? {} : { dismissed }),
      // 🚨 SPREAD THE EXISTING EXTRAS. Grade and notes ride the JSONB blob
      // alongside the session mode and the mirror link; rebuilding it would drop
      // whatever this screen does not model. Same rule as the trade object.
      extras: { ...row.session.extras, ...extras },
    });
    if (res.error) { setSaveError(res.error); return; }
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1600);
  };

  // ── nothing imported yet ───────────────────────────────────────────────────
  if (rows.length === 0) {
    return (
      <div className="card pr-empty" data-testid="practice-empty">
        <h3>No practice sessions yet</h3>
        {/* Beta's copy, verbatim — it is the clearest statement anywhere of what
            the feature actually is, and it is the thing users get wrong. */}
        <p className="muted">
          Practice runs on <b>your own platform</b>. Replay a historical session in
          Sierra Chart or ATAS — your studies, your DOM, your habits — and the
          TradeCoach logger sends those fills here like any other trade.
        </p>
        <p className="muted">
          Then <b>pick the ones that were real practice</b> and import them as a session.
          Practice keeps its own copy to grade, exactly like Backtesting does — your
          original trades are never moved or changed.
        </p>
        <p className="muted pr-fine">
          Replay fills only stay out of your live stats if their <b>account</b> is set to
          “exclude from metrics” — the same switch SIM accounts use.
        </p>
        <p className="pr-absent">
          ⚠ The importer isn’t built here yet, so sessions can’t be created from this
          tab — only viewed and graded.
        </p>
      </div>
    );
  }

  return (
    <div className="pr-tab" data-testid="practice-tab">
      {/* ── the session list ───────────────────────────────────────────────── */}
      <div className="pr-bar">
        <div className="pr-chips">
          {visible.map((r) => (
            <button
              key={r.session.id} type="button"
              className={`pr-sess${r.session.id === selectedId ? ' on' : ''}${r.session.dismissed ? ' dim' : ''}`}
              onClick={() => setSelectedId(r.session.id)}
            >
              <span className="pr-sess-date">{r.session.marketDate || 'imported'}</span>
              <span className={`pr-sess-net ${r.summary.net >= 0 ? 'green' : 'red'}`}>
                {money(r.summary.net, 0)}
              </span>
              <span className="pr-sess-n">{r.summary.n}</span>
            </button>
          ))}
        </div>
        {dismissedCount ? (
          <button type="button" className="pr-toggle" onClick={() => setShowDismissed((v) => !v)}>
            {showDismissed ? 'Hide dismissed' : `Show dismissed (${dismissedCount})`}
          </button>
        ) : null}
      </div>

      {/* ⚠ NAMED, NOT HIDDEN. See the header. */}
      <p className="pr-absent">
        ⚠ Importing new sessions, the AI session review and the full day report
        aren’t here yet. What’s below reads and grades what you already have.
      </p>

      {selected ? (
        <>
          <div className="pr-head">
            <h3 className="pr-title">
              Session — {selected.session.marketDate || 'imported'}
              <span className="muted pr-count">
                {' · '}{selected.summary.n} trade{selected.summary.n === 1 ? '' : 's'}
              </span>
              <GradeChip grade={String(selected.session.extras['grade'] ?? '')} />
            </h3>
            <div className="pr-actions">
              <button
                type="button" className="btn"
                title="Hide this session from Practice without deleting it"
                onClick={() => void patch(selected, { dismissed: !selected.session.dismissed })}
              >
                {selected.session.dismissed ? 'Restore' : 'Dismiss'}
              </button>
              <button
                type="button" className="btn danger"
                onClick={() => {
                  // 🚨 THE CASCADE IS NAMED. Deleting a session discards every
                  // annotation made inside it, and re-importing the same trades
                  // does not bring the grading back.
                  const msg = `Delete this practice session?\n\nIts ${selected.summary.n} copied `
                    + `trade${selected.summary.n === 1 ? '' : 's'} go too, along with every grade `
                    + 'and note you made on them. Re-importing the same trades does not bring '
                    + 'that back.\n\nYour original trades are not affected.';
                  if (window.confirm(msg)) void removeSession(selected.session.id);
                }}
              >
                Delete
              </button>
            </div>
          </div>

          {/* 🚨 THE LABEL THAT KEEPS THIS HONEST. These figures are what the
              trader WOULD have made, in an app whose other screens are real. */}
          <div className="pr-strip" data-testid="practice-summary">
            <div className="pr-stat">
              <span className="pr-stat-k">Replay P&amp;L</span>
              <span className={`pr-stat-v ${selected.summary.net >= 0 ? 'green' : 'red'}`}>
                {money(selected.summary.net, 2)}
              </span>
              <span className="pr-stat-s">not real money</span>
            </div>
            <div className="pr-stat">
              <span className="pr-stat-k">Win rate</span>
              <span className="pr-stat-v">
                {selected.summary.winRate == null ? '—' : pct(selected.summary.winRate, 0)}
              </span>
              <span className="pr-stat-s">
                {selected.summary.wins}W · {selected.summary.losses}L
              </span>
            </div>
            <div className="pr-stat">
              <span className="pr-stat-k">Graded</span>
              <span className="pr-stat-v">
                {selected.summary.annotated} of {selected.summary.n}
              </span>
              <span className="pr-stat-s">
                {selected.summary.annotated === selected.summary.n
                  ? 'every rep reviewed'
                  : 'your grade covers a partial review'}
              </span>
            </div>
          </div>

          {/* ── the copied trades ──────────────────────────────────────────── */}
          <div className="card pr-trades">
            <table className="pr-table">
              <thead>
                <tr>
                  <th className="pr-l">Contract</th><th className="pr-l">Side</th>
                  <th>Replay P&amp;L</th><th>MAE</th><th>MFE</th>
                  <th className="pr-l">Grade</th><th className="pr-l">Note</th>
                </tr>
              </thead>
              <tbody>
                {selected.trades.map((t) => {
                  const v = t.trade as Record<string, unknown>;
                  const p = Number(v['netPnl']);
                  return (
                    <tr key={t.id}>
                      <td className="pr-l">{String(v['contract'] ?? '—')}</td>
                      <td className="pr-l">{String(v['side'] ?? '—')}</td>
                      <td className={Number.isFinite(p) ? (p >= 0 ? 'green' : 'red') : 'muted'}>
                        {Number.isFinite(p) ? money(p, 2) : '—'}
                      </td>
                      {/* ⚠ Em-dash for absent, never zero — a trade with no
                          excursion recorded is not a trade that never moved. */}
                      <td>{v['maePoints'] == null ? '—' : num(Number(v['maePoints']), 2)}</td>
                      <td>{v['mfePoints'] == null ? '—' : num(Number(v['mfePoints']), 2)}</td>
                      <td className="pr-l">{String(v['grade'] ?? '') || '—'}</td>
                      <td className="pr-l pr-note">{String(v['notes'] ?? '') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="pr-absent pr-fine">
              ⚠ Grading an individual trade opens the trade form in beta. That
              wiring isn’t here yet — this is the read.
            </p>
          </div>

          {/* ── the review ─────────────────────────────────────────────────── */}
          <div className="card pr-review">
            <h4 className="pr-h4">Your review</h4>
            <label className="field">
              <span className="field-label">What were you drilling?</span>
              <input
                type="text" maxLength={140}
                defaultValue={selected.session.label}
                placeholder="e.g. hold winners past 1R — don’t touch the stop"
                onBlur={(e) => void patch(selected, { label: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field-label">Session grade</span>
              <select
                value={String(selected.session.extras['grade'] ?? '')}
                onChange={(e) => void patch(selected, { grade: e.target.value })}
              >
                <option value="">—</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <label className="field">
              <span className="field-label">How did this rep go?</span>
              <textarea
                rows={4}
                defaultValue={String(selected.session.extras['notes'] ?? '')}
                placeholder="Did you do the thing you were drilling? What broke, and what carries into live trading under real P&L pressure?"
                onBlur={(e) => void patch(selected, { notes: e.target.value })}
              />
            </label>
            <div className="pr-saverow">
              {saveError ? <span className="pr-err" role="alert">{saveError}</span> : null}
              {saved && !saveError ? <span className="pr-saved">Saved</span> : null}
              <span className="muted pr-fine">
                Saves when you click away from a field.
              </span>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
